<script setup>
import { storeToRefs } from 'pinia/dist/pinia';
import { useThemeStore } from '@/stores/themeStore';
import { computed } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({
    label: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        default: 'button', // button | submit
    },
    outlined: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    severity: {
        type: String,
        default: 'primary',
    },
    icon: {
        type: String,
        required: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    text: {
        type: Boolean,
        default: false,
    },
    /*
        TODO:
            I think "link" property should be removed, and instead, use "<a>" or any link-related tag. Quite redundant
            and doesn't make sense. Since aesthetically wise, they're just the same with text. But retained for now as
            there are some components that still use it.
    */
    /** @deprecated */
    link: {
        type: Boolean,
        default: false,
    },
    rounded: {
        type: Boolean,
        default: false,
    },
});

const
    themeStore = useThemeStore(),
    { themeColors } = storeToRefs(themeStore);

/** This will automatically set the severity to secondary if outlined is true. */
const severityHandler = computed(() => props.outlined ? 'secondary' : props.severity);

const btnPt = computed(() => {
    const mainColor = themeColors.value.main;

    if (!mainColor || props.outlined) return undefined;

    if (props.text || props.link) {
        return {
            root: {
                style: {
                    color: `#${mainColor}`,
                },
            },
        };
    }

    return {
        root: {
            style: {
                backgroundColor: `#${mainColor}`,
                borderColor: `#${mainColor}`,
            },
        },
    };
});
</script>

<template>
    <Button
        :label="props.label"
        :type="props.type"
        :outlined="props.outlined"
        :disabled="props.disabled"
        :severity="severityHandler"
        :icon="props.icon"
        :loading="props.loading"
        :pt="btnPt"
        :text="props.text"
        :link="props.link"
        :rounded="props.rounded"
    >
        <template #default>
            <slot />
        </template>

        <template #icon>
            <slot name="icon" />
        </template>
    </Button>
</template>

<style scoped>
.p-button:enabled:focus {
    box-shadow: 0 0 0 0.15rem v-bind('themeColors?.main ? `#${themeColors?.main}` : ""') !important;
}
</style>
